import React, { Component } from 'react';
import { oneOfType, number, string, bool, arrayOf, func, shape, any, oneOf } from 'prop-types';
import AccessControlList from '../AccessControlList';
import UserSelector from '../AutocompleteSelector/UserSelector';
import { 
  Dialog, 
  DialogContent, 
  DialogActions, 
  Button, 
  IconButton, 
  Tooltip, 
  Popover, 
  MenuItem, 
  ListItemText, 
  ListItemIcon 
} from '@material-ui/core';
import { Info, Close, Edit, RemoveRedEye, ArrowDropDown } from '@material-ui/icons';
import { OWNER, MANAGER, EDITOR, VIEWER, INHERIT, DIALOG_SHARE_ADVERTISER, DIALOG_SHARE_CAMPAIGN } from '../../helpers/constant';
import { get, cloneDeep, isEqual, orderBy, uniq, indexOf, includes } from 'lodash';
import { Status } from '../../state/modules/app/advertiser';


import styles from './styles.scss';
import classnames from 'classnames';

export class SharingDialog extends Component {
  static propTypes = {
    id: oneOfType([number, string]),
    dialogName: oneOf([DIALOG_SHARE_ADVERTISER, DIALOG_SHARE_CAMPAIGN]),
    open: bool,
    label: string,
    description: string,
    entityName: string,
    approvedListTitle: string,
    ownerId: string,
    currentUserId: string,
    editors: arrayOf(string),
    viewers: arrayOf(string),
    inheritIds: arrayOf(string),
    users: arrayOf(shape({
      id: number,
      name: string,
      email:string
    })).isRequired,
    usersHashMap: shape({any}).isRequired,
    memberManagerHashMap: shape({any}).isRequired,
    autoCompleteTitle: string,
    autoCompleteSubtitle: string,
    setEditLabel: string,
    setViewLabel: string,
    btnAddLabel: string,
    btnCancelLabel: string,
    btnDoneLabel: string,
    className: string,
    onClose: func,
    onChange: func,
    loadOrgUsers: func,
    loadTeams: func,
    loadAdvertisers: func,
    loadCampaigns: func,
    advertiserLoadStatus: string,
    campaignLoadStatus: string,
    userLoadStatus: oneOf(Object.values(Status)),
    teamLoadStatus: oneOf(Object.values(Status)),
  }

  static defaultProps = {
    description: 'You can share with others so they can have access to the same information as you',
    approvedListTitle: 'Who has access to ${entityName}',
    autoCompleteTitle: 'Give access to other people',
    autoCompleteSubtitle: 'Grants access to this Advertiser and all underlying Campaigns',
    setEditLabel: 'Can edit',
    setViewLabel: 'Can view',
    btnAddLabel: 'ADD',
    btnCancelLabel: 'CANCEL',
    btnDoneLabel: 'DONE'
  }

  state = {
    newSelectedIds: [],
    accessRole: EDITOR,
    enableAddBtn: false,
    disableSubmit: true,
    anchorEl: null
  }

  static getDerivedStateFromProps(props, prevState) {
    const { ownerId, editors, viewers, inheritIds =[] } = props;
    const newState = cloneDeep(prevState);
    if (!isEqual(ownerId, prevState.originalOwnerId)) {
      newState.ownerId = ownerId;
      newState.originalOwnerId = ownerId;
    }

    if (!isEqual(editors, prevState.originalEditors)) {
      newState.editors = [...editors];
      newState.originalEditors = [...editors];
    }

    if (!isEqual(viewers, prevState.originalViewers)) {
      newState.viewers = [...viewers];
      newState.originalViewers = [...viewers];
    }

    if (!isEqual(inheritIds, prevState.inheritIds)) {
      newState.inheritIds = [...inheritIds];
    }
    return newState;
  }

  componentDidMount() {
    const { 
      loadOrgUsers, 
      loadTeams, 
      advertiserLoadStatus, 
      loadAdvertisers, 
      campaignLoadStatus, 
      loadCampaigns,
      teamLoadStatus,
      userLoadStatus
    } = this.props;

    if (
      userLoadStatus !== Status.STATUS_LOADING &&
      userLoadStatus !== Status.STATUS_LOADED
    ) {
      loadOrgUsers && loadOrgUsers();
    }

    if (
      teamLoadStatus !== Status.STATUS_LOADING &&
      teamLoadStatus !== Status.STATUS_LOADED
    ) {
      loadTeams && loadTeams();
    }

    if (
      advertiserLoadStatus && 
      advertiserLoadStatus !== Status.STATUS_LOADED && 
      advertiserLoadStatus !== Status.STATUS_LOADING
    ) {
      loadAdvertisers && loadAdvertisers();
    }

    if ( // TODO: double check if campaign has status
      campaignLoadStatus && 
      campaignLoadStatus !== Status.STATUS_LOADED && 
      campaignLoadStatus !== Status.STATUS_LOADING
    ) {
      loadCampaigns && loadCampaigns(); 
    }
  }

  //---------------------------------------------------------------------
  //------------------------Interaction Functions------------------------
  //---------------------------------------------------------------------

  handleChange = ({userId, permission}) => {
    const { editors, viewers, ownerId: oldOwnerId } = this.state;
    const newEditors = [...editors];
    const newViewers = [...viewers];
    let newOwnerId = oldOwnerId;
    const viwerIndex = indexOf(newViewers, userId);
    const editorIndex = indexOf(newEditors, userId);

    switch(permission) {
      case OWNER:
      {
        newOwnerId = userId;
        if (viwerIndex > -1) {
          newViewers.splice(viwerIndex, 1); 
        }
        if (editorIndex > -1) {
          newEditors.splice(editorIndex, 1); 
        }
        !includes(newEditors, oldOwnerId) && newEditors.push(oldOwnerId);
        break;
      }
      case EDITOR:
      {
        if (viwerIndex > -1) {
          newViewers.splice(viwerIndex, 1); 
        }
        newEditors.push(userId);
        break;
      }
      case VIEWER:
      {
        if (editorIndex > -1) {
          newEditors.splice(editorIndex, 1); 
        }
        newViewers.push(userId);
        break;
      }
      case null:
      {
        if (viwerIndex > -1) {
          newViewers.splice(viwerIndex, 1);
          break;
        }
        if (editorIndex > -1) {
          newEditors.splice(editorIndex, 1); 
          break;
        }
      }
    }

    this.setState({
      disableSubmit: false,
      editors: newEditors,
      viewers: newViewers,
      ownerId: newOwnerId
    });
  }

  handleSubmit = () => {
    const { id, onChange, usersHashMap, dialogName } = this.props;
    const { ownerId, editors, viewers } = this.state;
    const selectedUsers = [];

    if (dialogName === DIALOG_SHARE_ADVERTISER) {
      const owner = cloneDeep(usersHashMap[ownerId]);
      if (owner) {
        owner.role = OWNER;
        selectedUsers.push(owner);
      }
    }
    
    const editorsPool = editors.reduce((accum, editorId) => {
      if(usersHashMap[editorId]) {
        const editor = cloneDeep(usersHashMap[editorId]);
        editor.role = EDITOR;
        accum.push(editor);
      }
      return accum;
    }, []);

    const viewersPool = viewers.reduce((accum, viewerId) => {
      if(usersHashMap[viewerId]) {
        const viewer = cloneDeep(usersHashMap[viewerId]);
        viewer.role = VIEWER;
        accum.push(viewer);
      }
      return accum;
    }, []);
    selectedUsers.push(...editorsPool);
    selectedUsers.push(...viewersPool);
    onChange && onChange(id, selectedUsers);
    this.handleClose();
  }

  handleClose = () => {
    const { onClose, dialogName } = this.props;
    onClose && onClose(dialogName);
  }

  //======== handle autocompleteSelector =======//

  handleSelect = (newSelectedIds) => {
    this.setState({
      enableAddBtn: true,
      newSelectedIds
    });
  }

  handleAddBtnClick = () => {
    const { accessRole, newSelectedIds, editors, viewers } = this.state;
    const originalIds = accessRole == EDITOR? [...editors] : [...viewers];
    const stateKey = accessRole == EDITOR? 'editors': 'viewers';
    this.setState({
      [stateKey]: [...originalIds, ...newSelectedIds],
      enableAddBtn: false,
      newSelectedIds: [],
      disableSubmit: false
    });
  }

  handleCancel = () => {
    this.setState({
      enableAddBtn: false,
      newSelectedIds: []
    });
  }

  handlePopoverOpen = (e) => { //popover of userSelector
    this.setState({
      anchorEl: e.currentTarget
    });
  }

  handlePermissionChange = (newRole) => () => {
    const { accessRole } = this.state;
    if (accessRole !== newRole) {
      this.setState({
        accessRole: newRole
      });
    }
    this.handlePopoverClose();
  }

  handlePopoverClose = () => {
    this.setState({
      anchorEl: null
    });
  }

  //---------------------------------------------------------------------
  //---------------------------Draw Functions----------------------------
  //---------------------------------------------------------------------

  drawSharedList() {
    const { usersHashMap, memberManagerHashMap, dialogName, currentUserId } = this.props;
    const { ownerId, editors, viewers, inheritIds } = this.state;
    let users = {};
    users = getAccessUsers([ownerId], OWNER, usersHashMap, memberManagerHashMap, users);
    users = getAccessUsers(inheritIds, INHERIT, usersHashMap, memberManagerHashMap, users);
    users = getAccessUsers(editors, EDITOR, usersHashMap, memberManagerHashMap, users);
    users = getAccessUsers(viewers, VIEWER, usersHashMap, memberManagerHashMap, users);

    const currentUser = cloneDeep(users[currentUserId]);
    currentUser && delete users[currentUserId];
    const owner = cloneDeep(users[ownerId]);
    owner && delete users[ownerId];
    const usersPool = orderBy(Object.values(users), 'firstName', 'asc');
    const selectedUsers = [];
    currentUser && selectedUsers.push(currentUser);
    ownerId !== currentUserId && owner && selectedUsers.push(owner);
    usersPool && selectedUsers.push(...usersPool);

    return (
      <AccessControlList
        users={selectedUsers}
        onChange={this.handleChange}
        inCampaignDialog={dialogName == DIALOG_SHARE_CAMPAIGN}
        usersHashMap={usersHashMap}
        memberManagerHashMap={memberManagerHashMap}
      />
    )
  }

  drawUserSelector() {
    const {
      users,
      autoCompleteTitle, 
      autoCompleteSubtitle, 
      setEditLabel,
      setViewLabel,
    } = this.props;
    const { accessRole, newSelectedIds, ownerId, editors, viewers, inheritIds } = this.state;
    const tooltipText = accessRole === EDITOR? setEditLabel : setViewLabel;
    const allIds = [ownerId, ...editors, ...viewers, ...inheritIds]
    const dataPool = users && users.reduce((accum, user) => { 
      if (!includes(allIds, user.id)){
        accum.push(user);
      }
      return accum;
    }, []);

    return (
      <div className={styles.selector}>
        <div className={styles.selectorTitle}>{autoCompleteTitle}</div>
        <div className={styles.selectorSubtitle}>{autoCompleteSubtitle}</div>
        <div className={styles.selectorSearch}>
          <div className={styles.searchBar}>
            <UserSelector
              dataPool={dataPool}
              selectedIds={newSelectedIds}
              onChange={this.handleSelect}
            />
          </div>
          <Tooltip title={tooltipText}>
            <span className={styles.actionIcon} onClick={this.handlePopoverOpen}>
              {accessRole == EDITOR? <Edit data-test="sharingDialogSelectEditBtn"/> : <RemoveRedEye data-test="sharingDialogSelectViewIcon"/>}
              <ArrowDropDown data-test="shareDialogSelectTypeBtn" className={styles.arrowIcon}/>
            </span>
          </Tooltip>
          {this.drawSelectorPopover()}
        </div>
      </div>
    )
  }

  drawSelectorPopover() {
    const { setEditLabel, setViewLabel } = this.props;
    const { anchorEl } = this.state;
    return (
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={this.handlePopoverClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
      >
        <MenuItem onClick={this.handlePermissionChange(EDITOR)}>
          <ListItemIcon data-test="sharingDialogSelectEditItem">
            <Edit/>
          </ListItemIcon>
          <ListItemText>
            {setEditLabel}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={this.handlePermissionChange(VIEWER)} >
          <ListItemIcon data-test="sharingDialogSelectViewItem">
            <RemoveRedEye/>
          </ListItemIcon>
          <ListItemText>
            {setViewLabel}
          </ListItemText>
        </MenuItem>
      </Popover>
    )
  }

  drawAddActions() {
    const { btnAddLabel, btnCancelLabel } = this.props;
    return (
      <div className={styles.addAccess}>
        <Button
          data-test="sharingDialogAddBtn"
          className={styles.btnAdd}
          onClick={this.handleAddBtnClick} 
          color='primary'
          variant='contained'
        >
          {btnAddLabel}
        </Button>
        <Button data-test="sharingDialogCancelAddBtn" onClick={this.handleCancel}>{btnCancelLabel}</Button>
      </div>
    )
  }

  render() {
    const { open, label, description, entityName, approvedListTitle, btnCancelLabel, btnDoneLabel, className } = this.props;
    const { enableAddBtn, disableSubmit } = this.state;
    const listTitle = entityName && approvedListTitle.replace('${entityName}', entityName);

    return (
      <Dialog data-test="sharingDialog" open={open} className={classnames(styles.sharingDialog, className)} scroll='body'>
        <div className={styles.dialogTitle}>
          <div className={styles.title}>
            <span>{label}</span>
            <Tooltip title={description}>
              <span className={styles.icon}><Info/></span>
            </Tooltip>
          </div>
          <IconButton className={styles.closeBtn} onClick={this.handleClose}>
            <Close/>
          </IconButton>
        </div>
        <DialogContent className={styles.dialogContent}>
          <div className={styles.approvedListTitle}>{listTitle}</div>
          <div className={styles.sharedList}>
            {this.drawSharedList()}
          </div>
        </DialogContent>
        {this.drawUserSelector()}
        { enableAddBtn? 
          this.drawAddActions() :
          <DialogActions className={styles.dialogActions}>
            <Button data-test="sharingDialogCancelBtn" onClick={this.handleClose}>{btnCancelLabel}</Button>
            <Button
              data-test="sharingDialogSubmitBtn"
              onClick={this.handleSubmit} 
              color={disableSubmit? 'secondary' : 'primary'} 
              variant='contained' 
              disabled={disableSubmit}
            >
              {btnDoneLabel}
            </Button>
          </DialogActions>
        }
      </Dialog>
    )
  }
}

/** getAccessUsers
 * Permission heirachy: owner > manager > editor > viewer > inherit
 * @param {Array} userIds - Array of userId strings
 * @param {String} permission - one of MANAGER/EDITOR/VIEWER/INHERIT
 * @param {Object} usersHashMap - Hash map of all org users {[userId]: {...objUserInfo}}
 * @param {Object} memberManagerHashMap - Hash map of all users manager {[userId]:[member's managerIds]}
 * @param {Object} accessUsersHashMap - Hash map of exist access users {[userId]: {...objUserInfo with permission}}
 * @returns {Object} New accessUsersHashMap
 */
export function getAccessUsers(userIds, permission, usersHashMap, memberManagerHashMap, accessUsersHashMap) {
  const newAccessUsers = userIds && userIds.reduce((accum, currentId) => {
    const newUser = cloneDeep(usersHashMap[currentId]);
    if (newUser) {
      const arrManagers = uniq(get(memberManagerHashMap, currentId, []));
      arrManagers && arrManagers.forEach( managerId => {
        const objManager = cloneDeep(usersHashMap[managerId]);
        const currentRole = get(accum, [managerId, 'permission']);
        if (objManager && currentRole !== OWNER) {
          objManager.permission = MANAGER;
          accum[managerId] = objManager;
        }
      });
      const currentRole = get(accum, [currentId, 'permission']);
      if (currentRole !== MANAGER || permission == OWNER) {
        newUser.permission = permission;
        accum[currentId] = newUser;
      }
    }
    return accum;
  }, cloneDeep(accessUsersHashMap));
  return newAccessUsers;
}
